"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Combobox = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_1 = require("@headlessui/react");
const react_2 = require("react");
const Option_1 = require("./Option");
const constants_1 = require("../../constants");
const styles_1 = require("../../constants/styles");
const useValidate_1 = require("../../hooks/useValidate");
const translate_1 = require("../../translate");
const ErrorMessage_1 = require("../ErrorMessage");
const Form_1 = require("../Form");
const Icon_1 = require("../Icon");
const Button = (0, styled_1.default)(react_1.Combobox.Button)({
    display: 'none',
});
const ComboboxElement = styled_1.default.div({
    position: 'relative',
});
const ComboboxInput = (0, styled_1.default)(react_1.Combobox.Input, {
    shouldForwardProp: (propName) => !['small', 'invalid'].includes(propName),
})(styles_1.comboboxInputStyles);
const Options = (0, styled_1.default)(react_1.Combobox.Options)(({ alignRight, ...props }) => ({
    ...(0, styles_1.dropdownItemsStyles)(props),
    ...(alignRight && {
        left: undefined,
        right: 0,
    }),
    zIndex: constants_1.theme.zIndex.popover,
    // width: 'var(--button-width)' // NOT DEFINED!
}));
const InputContainer = styled_1.default.div({
    position: 'relative',
});
const StyledIcon = (0, styled_1.default)(Icon_1.Icon)({
    marginLeft: 8,
    pointerEvents: 'none',
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translate(-50%, -50%)',
});
const Combobox = (props) => {
    const [touched, setTouched] = (0, react_2.useState)(false);
    const validateError = (0, useValidate_1.useValidate)(props.schema, props.required, props.value);
    const buttonRef = (0, react_2.useRef)(null);
    const [query, setQuery] = (0, react_2.useState)('');
    const t = (0, translate_1.useTranslations)();
    const handleBlur = (event) => {
        if (!event.target.value && props.clearable) {
            props.onChange(undefined);
        }
    };
    const handleQueryChange = (event) => {
        setTouched(true);
        setQuery(event.target.value);
    };
    const handleValueChange = (value) => {
        if (props.clearable) {
            props.onChange(value ?? undefined);
        }
        else if (value !== null && value !== undefined) {
            props.onChange(value);
        }
        setQuery('');
    };
    const displayValue = props.displayValue ??
        props.options.find((option) => option.value === props.value)?.label ??
        '';
    const filteredOptions = props.options.filter((option) => {
        return option.label
            ?.trim()
            .toLowerCase()
            .includes(query.trim().toLowerCase());
    });
    const placeholder = props.placeholder ||
        (props.label ? `${props.label}...` : `${t.form.select_a_value()}...`);
    /**
     * Workaround for https://github.com/tailwindlabs/headlessui/discussions/1236.
     *
     * Makes the options open on focus.
     */
    const open = (isOpen, event) => {
        // !e -> not FocusEvent -> we should click input
        // e.relatedTarget -> there is an element losing focus (Option focused) -> don't click input
        if (!isOpen && (!event || event.relatedTarget)) {
            buttonRef.current?.click();
        }
        setQuery('');
    };
    const errorMessage = props.errorMessage ?? validateError;
    const showError = (touched || props.forceShowError) && !!errorMessage;
    return ((0, jsx_runtime_1.jsx)(react_1.Combobox, { disabled: props.disabled, onChange: handleValueChange, value: props.value ?? null, children: ({ open: isOpen }) => {
            const comboboxElement = ((0, jsx_runtime_1.jsxs)(ComboboxElement, { children: [(0, jsx_runtime_1.jsxs)(InputContainer, { children: [(0, jsx_runtime_1.jsx)(ComboboxInput
                            // We'll use "nope" as a nonsense value, as some browsers ignore "off":
                            , { 
                                // We'll use "nope" as a nonsense value, as some browsers ignore "off":
                                autoComplete: 'nope', cursor: isOpen ? 'text' : 'pointer', displayValue: () => displayValue, invalid: showError, onBlur: handleBlur, onChange: handleQueryChange, onFocus: (event) => open(isOpen, event), onKeyDown: () => open(isOpen), onMouseDown: () => open(isOpen), placeholder: placeholder, 
                                // @ts-ignore "small is not valid HTML attribute"
                                small: props.small, minWidth: props.minWidth, maxWidth: props.maxWidth, spellCheck: "false" }), (0, jsx_runtime_1.jsx)(StyledIcon, { name: "ChevronDownIcon", size: "sm" })] }), (0, jsx_runtime_1.jsx)(Button, { ref: buttonRef }), filteredOptions.length >= 1 && ((0, jsx_runtime_1.jsxs)(InputContainer, { children: [(0, jsx_runtime_1.jsx)(ErrorMessage_1.ErrorMessage, { errorMessage: showError ? errorMessage : undefined, reserveSpace: props.reserveErrorSpace }), (0, jsx_runtime_1.jsxs)(Options, { alignRight: props.alignRight, children: [props.clearable && ((0, jsx_runtime_1.jsx)(Option_1.ComboboxOption, { label: placeholder, placeholder: true, small: props.small, value: null })), filteredOptions.map((option) => ((0, jsx_runtime_1.jsx)(Option_1.ComboboxOption, { label: option.label, small: props.small, value: option.value }, String(option.value))))] })] }))] }));
            return props.label ? ((0, jsx_runtime_1.jsx)(Form_1.Form.Label, { icon: props.labelIcon, required: props.required, text: props.label, children: comboboxElement })) : (comboboxElement);
        } }));
};
exports.Combobox = Combobox;
